// ═══════════════════════════════════════════════════════════
// Home (App Shell) — Luxury Governmental Dark Navy Glassmorphism
// Orchestrates all views with animated page transitions
// ═══════════════════════════════════════════════════════════

import { useState, useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import BottomNav, { ViewType } from "@/components/BottomNav";
import HomeView from "@/components/HomeView";
import SearchView from "@/components/SearchView";
import BrowseView from "@/components/BrowseView";
import DetailView from "@/components/DetailView";
import SwipeBack from "@/components/SwipeBack";
import { Department, ALL } from "@/lib/data";

type SearchMode = "all" | "dept" | "head" | "floor";

const PAGE_VARIANTS = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.25, ease: [0, 0, 0.2, 1] as const } },
  exit: { opacity: 0, y: -6, transition: { duration: 0.15 } },
};

export default function Home() {
  const [view, setView] = useState<ViewType>("home");
  const [prevView, setPrevView] = useState<ViewType>("home");
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState<SearchMode>("all");
  const [selected, setSelected] = useState<Department | null>(null);

  const navigate = useCallback((v: ViewType) => {
    setPrevView(view);
    setView(v);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [view]);

  const handleSelect = useCallback((d: Department) => {
    setSelected(d);
    setPrevView(view);
    setView("detail");
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [view]);

  const handleBack = useCallback(() => {
    navigate(query ? "search" : "browse");
  }, [query, navigate]);

  const handleQueryChange = useCallback((q: string) => {
    setQuery(q);
    if (view !== "search") navigate("search");
  }, [view, navigate]);

  const handleModeChange = useCallback((m: SearchMode) => {
    setMode(m);
  }, []);

  // Deep link: ?d=DEPT_ID jumps straight to that department's detail
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("d");
    if (!id) return;
    const dept = ALL.find((x) => x.id === id);
    if (dept) {
      setSelected(dept);
      setView("detail");
    }
  }, []);

  return (
    <div
      className="relative max-w-[480px] mx-auto min-h-screen"
      style={{
        background: "var(--background)",
        paddingTop: "env(safe-area-inset-top, 0px)",
      }}
    >
      {/* Background z-index layer */}
      <div className="relative z-10 pb-[80px]">
        <AnimatePresence mode="wait">
          {view === "home" && (
            <motion.div key="home" {...PAGE_VARIANTS}>
              <HomeView onNavigate={navigate} />
            </motion.div>
          )}
          {view === "search" && (
            <motion.div key="search" {...PAGE_VARIANTS}>
              <SwipeBack onBack={() => navigate("home")}>
                <SearchView
                  query={query}
                  mode={mode}
                  onQueryChange={handleQueryChange}
                  onModeChange={handleModeChange}
                  onSelect={handleSelect}
                />
              </SwipeBack>
            </motion.div>
          )}
          {view === "browse" && (
            <motion.div key="browse" {...PAGE_VARIANTS}>
              <SwipeBack onBack={() => navigate("home")}>
                <BrowseView onSelect={handleSelect} />
              </SwipeBack>
            </motion.div>
          )}
          {view === "detail" && selected && (
            <motion.div key={`detail-${selected.id}`} {...PAGE_VARIANTS}>
              <SwipeBack onBack={handleBack}>
                <DetailView
                  dept={selected}
                  onBack={handleBack}
                  onSelect={handleSelect}
                />
              </SwipeBack>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Bottom navigation */}
      <BottomNav view={view} onNavigate={navigate} />
    </div>
  );
}
